import { queryOptions } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { Insert, Tables, Update } from "@/types/database";
import { qk } from "./_queryKeys";
import { assertData, throwIfError } from "./_supabaseErrors";
import { excludeTestData } from "./testData";
import { insertWithWriteSource, type WriteSource } from "./writeSource";
import { reindexExpenseSummaries } from "./knowledge";
import { matchExpenseToProcurement } from "./procurement";

export type Expense = Tables<"expenses">;
export type ExpenseInsert = Insert<"expenses">;
export type ExpenseUpdate = Update<"expenses">;

/**
 * Mentés után: kiadás-összesítők újraindexelése és a beszerzési tétel
 * megjelölése. Best-effort, a mentés már sikerült.
 */
function scheduleExpenseSideEffects(row: Expense, options?: { match?: boolean }): void {
  void reindexExpenseSummaries().catch(() => {});
  if (options?.match === false) return;
  void matchExpenseToProcurement({
    id: row.id,
    title: row.title,
    phase_id: row.phase_id,
    task_id: row.task_id
  }).catch(() => {});
}

export async function listExpenses(): Promise<Expense[]> {
  const { data, error } = await supabase.from("expenses").select("*").order("date", { ascending: false });
  throwIfError(error);
  return excludeTestData(data);
}

export async function listExpensesByPhase(phaseId: string): Promise<Expense[]> {
  const { data, error } = await supabase
    .from("expenses")
    .select("*")
    .eq("phase_id", phaseId)
    .order("date", { ascending: false });
  throwIfError(error);
  return excludeTestData(data);
}

export async function listExpensesByTask(taskId: string): Promise<Expense[]> {
  const { data, error } = await supabase
    .from("expenses")
    .select("*")
    .eq("task_id", taskId)
    .order("date", { ascending: false });
  throwIfError(error);
  return excludeTestData(data);
}

export async function getExpense(id: string): Promise<Expense> {
  const { data, error } = await supabase.from("expenses").select("*").eq("id", id).single();
  throwIfError(error);
  return assertData(data, "Expense not found");
}

export async function createExpense(values: ExpenseInsert, source: WriteSource = "ui"): Promise<Expense> {
  const row = await insertWithWriteSource<Expense>(
    "expenses",
    values as unknown as Record<string, unknown>,
    source
  );
  scheduleExpenseSideEffects(row);
  return row;
}

export async function updateExpense(id: string, values: ExpenseUpdate): Promise<Expense> {
  const { data, error } = await supabase
    .from("expenses")
    .update(values)
    .eq("id", id)
    .select("*")
    .single();
  throwIfError(error);
  const row = assertData(data, "Expense not found");
  scheduleExpenseSideEffects(row, { match: Object.prototype.hasOwnProperty.call(values, "title") });
  return row;
}

export async function deleteExpense(id: string): Promise<void> {
  const { error } = await supabase.from("expenses").delete().eq("id", id);
  throwIfError(error);
  void reindexExpenseSummaries().catch(() => {});
}

/** Duplikátum-figyelmeztetéshez: a rögzítés előtt látott közeli tételek. */
export async function listExpensesForDuplicateCheck(amount: number): Promise<Expense[]> {
  const { data, error } = await supabase
    .from("expenses")
    .select("*")
    .gte("amount", amount - 0.01)
    .lte("amount", amount + 0.01)
    .limit(20);
  throwIfError(error);
  return data ?? [];
}

export const expensesQueryOptions = () =>
  queryOptions({
    queryKey: qk.expenses,
    queryFn: listExpenses
  });

export const expensesByPhaseQueryOptions = (phaseId: string) =>
  queryOptions({
    queryKey: [...qk.expenses, { phaseId }] as const,
    queryFn: () => listExpensesByPhase(phaseId)
  });

export const expensesByTaskQueryOptions = (taskId: string) =>
  queryOptions({
    queryKey: [...qk.expenses, { taskId }] as const,
    queryFn: () => listExpensesByTask(taskId)
  });

export const expenseQueryOptions = (id: string) =>
  queryOptions({
    queryKey: qk.expense(id),
    queryFn: () => getExpense(id)
  });

